import { useRef } from "react"

import useCollection from "./useCollection"
import useListFetchURL from "./useListFetchURL"
import preloadSWRCache from "./usePreloadSWRCache"
import { pluralize } from "web/services/inflector"

// pre load cache only if view or ids haven't changed
const LIST_IDENTITY = [
  1, // view type
  2, // view id
  12, // ids
  13, // view modification id
]

const EMPTY_DETAIL = Object.freeze(
  Object.assign([], {
    refresh: () => {},
    isLoading: false,
    isValidating: false,
  })
)
const EMPTY_RESULT = Object.freeze({ detail: EMPTY_DETAIL })

export default function useObjectList({
  subjectType,
  subjectId,
  parentSubjectType,
  parentSubjectId,
  searchQuery,
  topic,
  view = {},
  page = 1,
  url,
  ids = [],
  enableLocationIndex = false,
  enabled = true,
  includeLocationOrdinals = false,
  swrOptions = {},
}) {
  const {
    type: viewType,
    id: viewId,
    currency: viewCurrency,
    area_unit: viewAreaUnit,
    length_unit: viewLengthUnit,
    distance_unit: viewDistanceUnit,
    viewModificationId,
    fields: viewFields,
    conditions = [],
    sortField = null,
    sortDesc = null,
    groupField = null,
    groupDesc = null,
    secondGroupField = null,
    secondGroupDesc = null,
    mapCategoryFieldId = null,
  } = view

  const fetchURL = useListFetchURL({
    subjectType,
    subjectId,
    topic,
    parentSubjectType,
    parentSubjectId,
    url,
    enableLocationIndex,
  })

  const viewConditionsKey = conditions
    .filter((c) => c.value || c.empty_or_not_empty_operator)
    .map((c) => `${c.id}-${c.updated_at}`)
    .join("/")

  const viewFieldsKey = viewFields.map((vf) => vf.field.id).join("/")

  const lastFetchKey = useRef()

  const fetchKey = [
    fetchURL,
    viewType,
    viewId,
    searchQuery,
    sortField,
    sortDesc,
    groupField,
    groupDesc,
    secondGroupField,
    secondGroupDesc,
    mapCategoryFieldId,
    page,
    ids.join("."),
    viewModificationId,
    viewCurrency?.code,
    viewAreaUnit?.code,
    viewLengthUnit?.code,
    viewDistanceUnit?.code,
    viewConditionsKey,
    viewFieldsKey,
    view.table.use_averages,
    view.table.rows_per_page,
    includeLocationOrdinals,
  ]

  preloadSWRCache(LIST_IDENTITY, lastFetchKey.current, fetchKey)

  const fetchParams = {
    view_type: viewType,
    view_id: viewId,
    search_query: searchQuery,
    page: page,
    ids,
    include_location_ordinals: includeLocationOrdinals || undefined,
  }

  const updatePath =
    topic === undefined
      ? `/${pluralize(subjectType)}`
      : `/topics/${topic}/records`

  const response = useCollection({
    key: enabled ? fetchKey : null,
    url: fetchURL,
    resourceType: subjectType,
    updatePath,
    params: fetchParams,
    withMutateFns: !enableLocationIndex,
    swrOptions,
  })

  lastFetchKey.current = fetchKey

  if (!enabled) return EMPTY_RESULT
  if (Array.isArray(response)) return { detail: response }

  return response
}
